import React, { useEffect, useMemo, useState } from 'react';
import PolIcon from '../../../assets/icons/main/polCoin.svg';
import FrozenIcon from '../../../assets/icons/dashboard/programs/levelCard/frozenIcon.svg';
import HistoryIcon from '../../../assets/icons/level/footer/cyclesIcon.svg';
import { PROGRAM_NAMES, getLevelPriceWithCommission, getLevelPriceWithWeekly } from '../../../helpers/constants';
import { useUpgradeLvl } from '../../../helpers/hooks/useUpgradeLvl';
import { Button } from '../../../components/Button';
import { useCallTransaction } from '../../../helpers/hooks/useCallTransaction';
import { usePreviewMode } from '../../../helpers/hooks/useCheckPreviewMode';
import { round } from '../../../helpers/numbers';
import { BuyWithOptionals } from '../../../components/Modals/BuyWithOptionals';
import { PartnersPlaceModal } from '../../../components/Modals/PartnersPlaceModal';
import { useModal } from '../../../helpers/hooks/useModal';
import { MatrixPlaces, getDisplayPlaces } from './MatrixPlaces';

export const LevelCard = ({
  lvl,
  itemIndex,
  type,
  matrixs,
  isFirstAfterActive,
  isSecondAfterActive,
  isActivated,
  lastIncomeLevel,
  totalPartners,
  totalTeam,
  totalReward,
  maxActiveLevel,
  setIsNeedToCall,
  blocked,
  missedPartners,
  missedProfit,
  recyclesTotal,
}) => {
  const { previewUserAddress } = usePreviewMode();
  const { upgradeLvl } = useUpgradeLvl();
  const { onCallTransaction, transactionInfo, resetTransactionInfo } = useCallTransaction();
  const [isBuyModalOpened, setIsBuyModalOpened] = useState(false);
  const { openedModal, onOpen, onClose } = useModal();

  const cyclePlaces = useMemo(() => getDisplayPlaces(matrixs, type, true, recyclesTotal), [matrixs, type, recyclesTotal]);
  const filledPlaces = cyclePlaces.filter((place) => !!place?.user).length;
  const totalPlaces = type === PROGRAM_NAMES.X4 ? 6 : 2;

  const isAllowToBuyWithWeekly = useMemo(() => {
    return Number(lastIncomeLevel) === Number(maxActiveLevel) && maxActiveLevel > 0;
  }, [lastIncomeLevel, maxActiveLevel]);

  const upgradeClick = async (levelsX2, levelsX4, activateIncome = null) => {
    try {
      const shouldActivateIncome = activateIncome !== null ? activateIncome : isAllowToBuyWithWeekly;
      const result = await upgradeLvl(levelsX2, levelsX4, shouldActivateIncome);

      if (result) {
        onCallTransaction(result);
      }
    } catch (e) {
      console.log(e);
    }
  };

  const handleBuyClick = () => {
    setIsBuyModalOpened(true);
  };

  const handleBuyWithWeekly = (levelsX2, levelsX4) => {
    setIsBuyModalOpened(false);
    upgradeClick(levelsX2, levelsX4, true);
  };

  const handleBuyOnlyLevel = (levelsX2, levelsX4) => {
    setIsBuyModalOpened(false);
    upgradeClick(levelsX2, levelsX4, false);
  };

  useEffect(() => {
    if (transactionInfo.isSuccess) {
      setIsNeedToCall();
      resetTransactionInfo();
    }
  }, [transactionInfo]);

  const freezeLevelsX2 = type === PROGRAM_NAMES.X2 ? [maxActiveLevel + 1] : [];
  const freezeLevelsX4 = type === PROGRAM_NAMES.X4 ? [maxActiveLevel + 1] : [];

  const levelsX2 = useMemo(() => {
    return type === PROGRAM_NAMES.X2 ? [maxActiveLevel + 1] : [];
  }, [type, maxActiveLevel]);

  const levelsX4 = useMemo(() => {
    return type === PROGRAM_NAMES.X4 ? [maxActiveLevel + 1] : [];
  }, [type, maxActiveLevel]);

  const buyButton = useMemo(() => {
    return (
      <Button
        type="purple"
        loading={transactionInfo.isWaiting}
        loadingType="white"
        disabled={transactionInfo.isWaiting || previewUserAddress}
        onClick={isAllowToBuyWithWeekly ? handleBuyClick : () => upgradeClick(levelsX2, levelsX4)}
        className="flex items-center justify-center flex-nowrap min-w-[8.6rem] h-[4rem] bg-[#985AFF] rounded-[3rem] shadow-[0px_0px_40px_0px_rgba(152,90,255,0.30)] lg:w-full"
      >
        <div className="flex items-center justify-center flex-nowrap space-x-[0.4rem] text-[1.4rem] font-500 leading-[1.96rem] text-white">
          <span>Activate for</span>
          <div className="flex items-center justify-center">
            {getLevelPriceWithCommission(type, maxActiveLevel)}{' '}
            <img className="w-[1.6rem] h-[1.6rem] ml-[0.4rem]" src={PolIcon} />
          </div>
        </div>
      </Button>
    );
  }, [
    type,
    maxActiveLevel,
    transactionInfo,
    isAllowToBuyWithWeekly,
    levelsX2,
    levelsX4,
  ]);

  const historyButton = (
    <Button
      type="gray"
      onClick={onOpen}
      className="flex items-center justify-center flex-nowrap min-w-[8.6rem] h-[4rem] rounded-[3rem] lg:w-full"
    >
      <div className="flex items-center justify-center space-x-[0.6rem] text-[1.4rem] font-500 leading-[1.96rem] text-white">
        <img className="w-[1.6rem] h-[1.6rem]" src={HistoryIcon} alt="" />
        <span>History</span>
      </div>
    </Button>
  );

  const statusBadge = () => {
    if (blocked) {
      return (
        <div className="flex items-center justify-center h-[2.8rem] pl-[1rem] pr-[0.5rem] rounded-[2rem] bg-[rgba(106,190,255,0.10)] text-[#6ABEFF] space-x-[0.4rem]">
          <span className="text-[1.2rem] font-500 leading-[1.68rem]">Frozen</span>
          <img src={FrozenIcon} alt="Frozen" />
        </div>
      );
    }

    if (missedPartners > 0) {
      return (
        <div className="flex items-center space-x-[1rem] pl-[1.2rem] h-[2.8rem] bg-white-100 rounded-full pr-[0.4rem]">
          <span className="text-[1.2rem] font-500 leading-[1.68rem] text-white-500">Missed Partners</span>
          <div className="flex items-center justify-center rounded-full bg-[#F55456] !w-[2rem] !h-[2rem]">
            <span className="text-[1.2rem] font-500 text-white leading-[1.68rem]">{missedPartners}</span>
          </div>
        </div>
      );
    }

    return (
      <div
        className={`flex items-center justify-center h-[2.8rem] px-[1.2rem] rounded-[2rem] ${
          isActivated ? 'bg-[rgba(81,246,137,0.10)] text-[#51F689]' : 'bg-white-100 text-white-500'
        }`}
      >
        <span className="text-[1.2rem] font-500 leading-[1.68rem]">{isActivated ? 'Activated' : 'Inactive'}</span>
      </div>
    );
  };

  const cardTitle = () => {
    return (
      <div className="flex items-center flex-wrap gap-[0.8rem] min-w-0">
        <span className="text-[1.6rem] text-white font-600 leading-[2.24rem]">Level {lvl}</span>
        <div className="flex-shrink-0 flex items-center justify-center bg-white-100 rounded-[2rem] pl-[0.8rem] pr-[0.5rem] h-[2.8rem] space-x-[0.8rem]">
          <span className="text-[1.4rem] font-500 tracking-[-0.28px] text-white opacity-50 leading-none">Price</span>
          <span className="text-white text-[1.4rem] font-500 tracking-[-0.28px] leading-none">
            {getLevelPriceWithCommission(type, itemIndex)}
          </span>
          <img className="w-[1.8rem] h-[1.8rem]" src={PolIcon} />
        </div>
      </div>
    );
  };

  const statics = useMemo(() => {
    return (
      <div className={`flex flex-col space-y-[2.4rem] flex-1 min-w-0 lg:space-y-[1.6rem] ${isSecondAfterActive && 'opacity-50'}`}>
        <div className="grid grid-cols-3 gap-x-[2.4rem] gap-y-[1.6rem] lg:grid-cols-2 lg:gap-x-[1.2rem] lg:gap-y-[1.2rem]">
          <div className="flex flex-col space-y-[0.4rem] tracking-[-0.28px]">
            <span className="text-[1.2rem] text-white-500">Personal Partners</span>
            <span className="text-[1.4rem] text-white font-600">{totalPartners || 0}</span>
          </div>
          <div className="flex flex-col space-y-[0.4rem] tracking-[-0.28px]">
            <span className="text-[1.2rem] text-white-500">Team Partners</span>
            <span className="text-[1.4rem] text-white font-600">{totalTeam || 0}</span>
          </div>
          <div className="flex flex-col space-y-[0.4rem] tracking-[-0.28px]">
            <span className="text-[1.2rem] text-white-500">Restarts</span>
            <span className="text-[1.4rem] text-white font-600">{recyclesTotal || 0}</span>
          </div>
          <div className="flex flex-col space-y-[0.4rem] tracking-[-0.28px]">
            <span className="text-[1.2rem] text-white-500">Missed Profit</span>
            <span className="text-[1.4rem] text-white font-600">{round(Number(missedProfit || 0) / 1e18)}</span>
          </div>
          <div className="flex flex-col space-y-[0.4rem] tracking-[-0.28px]">
            <span className="text-[1.2rem] text-white-500">Current Cycle</span>
            <span className="text-[1.4rem] text-white font-600">
              {filledPlaces}/{totalPlaces}
            </span>
          </div>
        </div>
        <div className="flex w-full items-end justify-between lg:flex-col lg:items-start lg:gap-[1.2rem]">
          <div className="flex flex-col space-y-[0.5rem]">
            <span className="text-[1.2rem] text-white-500">Profit</span>
            <div className="flex items-center space-x-[0.8rem]">
              <span className="text-[2.4rem] text-white font-600 leading-none">{round(totalReward / 1e18)}</span>
              <img className="w-[2rem] h-[2rem]" src={PolIcon} alt="" />
            </div>
          </div>
          {(isActivated || isFirstAfterActive) && (
            <div className="flex items-center gap-[0.8rem] lg:w-full lg:flex-col">
              {isActivated && !blocked && historyButton}
              {isFirstAfterActive && buyButton}
            </div>
          )}
        </div>
      </div>
    );
  }, [
    isSecondAfterActive,
    totalPartners,
    totalTeam,
    totalReward,
    isFirstAfterActive,
    buyButton,
    historyButton,
    isActivated,
    blocked,
    recyclesTotal,
    missedProfit,
    filledPlaces,
    totalPlaces,
  ]);

  return (
    <div
      className={`relative w-full rounded-[2rem] overflow-hidden ${
        isActivated && 'border-[0.3rem] border-white-50 border-solid'
      }`}
    >
      <div
        className={`flex items-stretch justify-between gap-[3.2rem] w-full p-[2rem] lg:flex-col lg:items-stretch lg:gap-[1.6rem] lg:p-[1.6rem] ${
          isActivated ? 'bg-white-40' : 'bg-white-20'
        }`}
      >
        <div className="hidden lg:flex items-center justify-between gap-[0.8rem] w-full">
          {cardTitle()}
          {statusBadge()}
        </div>
        <div className="flex flex-col justify-between flex-1 min-w-0 space-y-[2.4rem] lg:space-y-[1.6rem]">
          <div className="lg:hidden">{cardTitle()}</div>
          {(isActivated || isFirstAfterActive) && statics}
          {isSecondAfterActive && statics}
          {blocked && (
            <button
              onClick={isAllowToBuyWithWeekly ? handleBuyClick : () => upgradeClick(freezeLevelsX2, freezeLevelsX4)}
              className="flex items-center justify-center bg-[rgba(106,190,255,0.10)] h-[4rem] rounded-[2rem] px-[1.6rem] w-fit lg:w-full"
            >
              <span className="text-[1.4rem] font-500 text-[#6ABEFF]">Activate level {itemIndex + 2} to unlock</span>
            </button>
          )}
        </div>
        <div className="flex flex-col items-end justify-between shrink-0 gap-[1.6rem] lg:hidden">
          {statusBadge()}
          {isActivated && !blocked && <MatrixPlaces type={type} matrixs={matrixs} recyclesTotal={recyclesTotal} />}
        </div>
        {isActivated && !blocked && (
          <div className="hidden lg:flex items-center justify-center w-full pt-[0.4rem]">
            <div className="flex items-center justify-center w-fit mx-auto rounded-[1.6rem] bg-white-50 px-[1.6rem] py-[1.2rem]">
              <MatrixPlaces type={type} matrixs={matrixs} recyclesTotal={recyclesTotal} />
            </div>
          </div>
        )}
        {isActivated && !blocked && (
          <img
            className="pointer-events-none absolute bottom-0 right-0"
            src={`/images/dashboard/programs/levelCard/${
              type === PROGRAM_NAMES.X2 ? 'greenShadow' : 'orangeShadow'
            }.webp`}
            alt=""
          />
        )}
        {blocked && (
          <img
            className="pointer-events-none absolute bottom-0 right-0"
            src="/images/dashboard/programs/levelCard/blueShadow.webp"
            alt=""
          />
        )}
      </div>
      {openedModal && (
        <PartnersPlaceModal
          level={lvl}
          type={type}
          matrixs={matrixs}
          openedModal={openedModal}
          handleCloseModal={onClose}
        />
      )}
      <BuyWithOptionals
        isOpened={isBuyModalOpened}
        onClose={() => setIsBuyModalOpened(false)}
        onBuyWithWeekly={() => {
          if (blocked) {
            handleBuyWithWeekly(freezeLevelsX2, freezeLevelsX4);
          } else {
            handleBuyWithWeekly(levelsX2, levelsX4);
          }
        }}
        onBuyOnlyLevel={() => {
          if (blocked) {
            handleBuyOnlyLevel(freezeLevelsX2, freezeLevelsX4);
          } else {
            handleBuyOnlyLevel(levelsX2, levelsX4);
          }
        }}
        totalPriceLevel={getLevelPriceWithCommission(type, maxActiveLevel)}
        totalPriceWithWeekly={getLevelPriceWithWeekly(type, maxActiveLevel)}
      />
    </div>
  );
};
